import * as React from 'react';
import { TransProps, useTranslation } from 'react-i18next';

import { Color, DetailsTableValueType, IconName } from '../../enums';
import { MenuPositionData, Status, TableDetailsRowData } from '../../interfaces';
import { isMenuPositionDataType, isStatusType } from '../../typeGuards';
import { Badge } from '../badge/badge.component';
import { Icon } from '../icon/icon.component';

import {
  Container,
  ImgLink,
  InfoContainer,
  InfoLabel,
  Label,
  LinkContainer,
  Row,
  SimpleValue,
  StyledIcon,
  TextValue,
  Value,
  ValueContainer
} from './detailsTable.styled';
import { DetailsTableProps } from './detailsTable.types';

export const DetailsTable: React.FC<DetailsTableProps> = (props: DetailsTableProps): JSX.Element => {
  const { className, rows }: DetailsTableProps = props;
  const { t }: TransProps<never> = useTranslation();

  const getValue: (
    value: string | string[] | Status | MenuPositionData[], type?: DetailsTableValueType,
  ) => JSX.Element | JSX.Element[] = (
    value: string | string[] | Status | MenuPositionData[], type: DetailsTableValueType = DetailsTableValueType.Text,
  ): JSX.Element | JSX.Element[] => {
    if (
      type === DetailsTableValueType.Status
      && (typeof value === 'object')
      && (!Array.isArray(value))
      && isStatusType(value)
    ) {
      return <Badge color={value.color} background={value.background} label={value.label} />;
    } else if (
      type === DetailsTableValueType.Image
      && (
        typeof value === 'string'
        || (Array.isArray(value) && (value as []).every((item: unknown): boolean => typeof item === 'string'))
      )
    ) {
      const arrayWithValues: string[] = Array.isArray(value) ? (value as string[]) : [value];
      return arrayWithValues.length ? (
        arrayWithValues.map((imgPath: string, imgIndex: number): JSX.Element => (
          <ImgLink href={imgPath} target='_blank' key={`${imgPath}${imgIndex}`}>
            <Icon name={IconName.Download} size={20} color={Color.Error500} />
            <SimpleValue>{`${t('onarte.ui.detailsTable.imageLabel')} ${imgIndex+1}`}</SimpleValue>
          </ImgLink>
        ))
      ) : <>-</>;
    } else if (type === DetailsTableValueType.Url && (Array.isArray(value) || (typeof value === 'string'))) {
      const arrayWithValues: (string | MenuPositionData)[] = Array.isArray(value) ? value : [value];
      const valuesAsMenuPositionData: MenuPositionData[] = arrayWithValues.map((item: string | MenuPositionData): MenuPositionData => {
        if (isMenuPositionDataType(item)) {
          return {
            shouldOpenInNewTab: true,
            ...item
          };
        }

        return {
          externalUrl: item,
          label: item,
          shouldOpenInNewTab: true
        };
      });

      return valuesAsMenuPositionData.length ? (
        valuesAsMenuPositionData.map((item: MenuPositionData, index: number): JSX.Element => (
          <LinkContainer {...item} key={`${item.label ?? ''}${index}`}>
            <StyledIcon name={IconName.InsertLink} size={20} color={Color.Error500} />
            <SimpleValue>{item.label}</SimpleValue>
          </LinkContainer>
        ))
      ) : <>-</>;
    } else {
      return <TextValue>{value}</TextValue>;
    }
  };

  return (
    <Container className={className}>
      {rows.map((row: TableDetailsRowData, index: number): JSX.Element => (
        <React.Fragment key={row.label}>
          <Row $isOdd={((index + 1) % 2 !== 0) && !row.newValue} $withNewValue={!!row.newValue}>
            <Label>{row.label}</Label>
            <ValueContainer>
              <Value $withPadding={row.type === DetailsTableValueType.Url || row.type === DetailsTableValueType.Image}>
                {getValue(row.value, row.type)}
              </Value>
              {!!row.newValue && (
                <InfoContainer>
                  <InfoLabel>{t('onarte.ui.detailsTable.currentLabel')}</InfoLabel>
                  <Icon name={IconName.Content} size={20} />
                </InfoContainer>
              )}
            </ValueContainer>
          </Row>
          {row.newValue && (
            <Row $withNewValue>
              <Label />
              <ValueContainer>
                <Value $withPadding={row.type === DetailsTableValueType.Url || row.type === DetailsTableValueType.Image}>
                  {getValue(row.newValue, row.type)}
                </Value>
                <InfoContainer>
                  <InfoLabel>{t('onarte.ui.detailsTable.newLabel')}</InfoLabel>
                  <Icon name={IconName.Warning} size={20} />
                </InfoContainer>
              </ValueContainer>
            </Row>
          )}
        </React.Fragment>
      ))}
    </Container>
  );
};
